import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Card, CardContent, CardHeader, Stack } from '@mui/material'
import Grid from '@mui/material/Grid2'
import DataAnalysisInfo from 'pages/DataAnalysis/components/DataAnalysisInfo'
import DataAnalysisFilters from '../../components/DataAnalysisFilters/DataAnalysisFilters'
import { MultiLineChart, Select } from 'components'
import {
  TIME_GRANULARITY,
  TParametersTrendData,
} from 'types/models/DataAnalysis'
import {
  getTabByKey,
  granularityOptions,
  prettyAttr,
  TAB_KEYS,
} from '../../utils/consts'
import {
  selectChartData,
  selectChartStatus,
  selectWheatColorByCodeMap,
} from 'store/selectors/dataAnalysisSelectors'
import {
  applyAndFetchChart,
  patchDraftFilters,
} from 'store/slices/dataAnalysisSlice'
import EmptyState from 'pages/DataAnalysis/components/EmptyState'
import { toParametersTrendChartData } from '../../transforms'
import { RootState } from 'store/store'
import * as sxStyles from '../tabs.styles'
import { DataTabsLayout, TrendChartStatus } from '../../layouts'
import { ChartExportWrapper } from '../../../../components'

const normalizeDataset = (
  raw: unknown
): Record<string, TParametersTrendData[]> => {
  if (!raw) return {}
  if (
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    'data' in (raw as any)
  ) {
    return normalizeDataset((raw as any).data)
  }
  if (Array.isArray(raw)) {
    return raw.length === 1
      ? (raw[0] as Record<string, TParametersTrendData[]>)
      : (raw as any)
  }
  return raw as Record<string, TParametersTrendData[]>
}

export const ParametersTrend = () => {
  const dispatch = useDispatch()
  const rawData = useSelector(selectChartData(TAB_KEYS.PARAMETERS_TREND))
  const status = useSelector(selectChartStatus(TAB_KEYS.PARAMETERS_TREND))
  const currentTab = getTabByKey(TAB_KEYS.PARAMETERS_TREND)
  const hasInfo = !!currentTab?.info?.length
  const colorByCode = useSelector(selectWheatColorByCodeMap)

  const draftFilters = useSelector(
    (state: RootState) =>
      state.dataAnalysis.byKey[TAB_KEYS.PARAMETERS_TREND]?.draft
  )

  const granularity = draftFilters?.time_granularity ?? TIME_GRANULARITY.DAILY

  useEffect(() => {
    if (!draftFilters?.time_granularity) {
      dispatch(
        patchDraftFilters({
          key: TAB_KEYS.PARAMETERS_TREND,
          patch: { time_granularity: TIME_GRANULARITY.DAILY },
        })
      )
    }
  }, [dispatch, draftFilters])

  const data = useMemo(() => normalizeDataset(rawData as any), [rawData])

  const charts = useMemo(
    () =>
      Object.entries(data).map(([attr, rows]) => ({
        attr,
        ...toParametersTrendChartData(rows, colorByCode),
      })),
    [data, colorByCode]
  )

  const hasAnyData = charts.some((c) => c.rows.length > 0)

  return (
    <DataTabsLayout
      sidebar={
        <DataAnalysisFilters
          filtersList={currentTab?.filters ?? []}
          chartKey={TAB_KEYS.PARAMETERS_TREND}
        />
      }
      tabKey={TAB_KEYS.PARAMETERS_TREND}
      onApply={() =>
        dispatch(applyAndFetchChart({ key: TAB_KEYS.PARAMETERS_TREND }))
      }
    >
      <TrendChartStatus status={status} title={TAB_KEYS.PARAMETERS_TREND}>
        <Stack alignItems="flex-end" width="100%">
          <Box
            sx={{
              paddingTop: 0.5,
              width: 250,
              paddingBottom: 0,
              marginBottom: 0,
            }}
          >
            <Select
              size="small"
              fullWidth
              label="Period"
              options={granularityOptions}
              value={granularity}
              onChange={(e) => {
                const g = e.target.value as TIME_GRANULARITY
                dispatch(
                  patchDraftFilters({
                    key: TAB_KEYS.PARAMETERS_TREND,
                    patch: { time_granularity: g },
                  })
                )
                dispatch(applyAndFetchChart({ key: TAB_KEYS.PARAMETERS_TREND }))
              }}
            />
          </Box>
        </Stack>
        {!hasAnyData ? (
          <EmptyState />
        ) : (
          <Grid container spacing={2} sx={{ marginTop: 0, paddingTop: 0 }}>
            {charts.map(({ attr, rows, series }) => {
              if (!rows.length) return null
              if (attr === 'gluten_index') return null

              return (
                <Grid key={attr} size={{ xs: 12, md: 6, lg: 6, xl: 6 }}>
                  <Card
                    data-exportable-chart="true"
                    variant="outlined"
                    sx={sxStyles.parametersComparisonCard}
                  >
                    <CardHeader
                      title={prettyAttr(attr)}
                      titleTypographyProps={{ fontSize: 24, fontWeight: 800 }}
                    />
                    <CardContent sx={sxStyles.parametersComparisonCardContent}>
                      <ChartExportWrapper title={prettyAttr(attr)}>
                        <MultiLineChart
                          data={rows}
                          series={series}
                          xDataKey="period"
                        />
                      </ChartExportWrapper>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
            {hasInfo && <DataAnalysisInfo infoList={currentTab.info} />}
          </Grid>
        )}
      </TrendChartStatus>
    </DataTabsLayout>
  )
}
